import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useIntl } from 'react-intl';
import NativeDatepickerClass from 'native-datepicker';
import { childrenShape } from '@bibliocommons/bc-prop-types';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

export default function NativeDatePicker({
  value,
  onChange,
  minDate = '',
  maxDate = '',
  className = '',
  id,
  children
}) {
  const intl = useIntl();

  const labelText = value
    ? `${intl.formatMessage({ id: 'select_a_date' })}, ${intl.formatDate(value, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}`
    : intl.formatMessage({ id: 'select_a_date' });

  const spanRef = useRef();
  const [datepicker, setDatepicker] = useState();
  const valueRef = useRef(value);
  const justFocusedRef = useRef(false);
  const focusTimerRef = useRef(null);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (spanRef.current && !datepicker) {
      const newDatepicker = new NativeDatepickerClass({
        existingElement: spanRef.current,
        initialValue: value || '',
        onChange: newValue => {
          // iOS fires onChange immediately when an empty date input receives
          // focus, auto-selecting today's date. We suppress this by ignoring onChange
          // events that fire within 300ms of focus when the picker had no value.
          // Real user selections happen after this window.
          if (justFocusedRef.current && !valueRef.current) {
            justFocusedRef.current = false;
            return;
          }
          justFocusedRef.current = false;
          onChange(newValue);
        }
      });

      newDatepicker.dateInputElement.addEventListener('focus', () => {
        justFocusedRef.current = true;
        clearTimeout(focusTimerRef.current);
        focusTimerRef.current = setTimeout(() => {
          justFocusedRef.current = false;
        }, 300);
      });

      setDatepicker(newDatepicker);
    }

    if (datepicker) {
      datepicker.fullValue = value || '';
      datepicker.setValue(datepicker.fullValue);
      datepicker.dateInputElement.id = id;
      if (minDate) datepicker.dateInputElement.min = minDate;
      if (maxDate) datepicker.dateInputElement.max = maxDate;
      // Need to hide the native input from screen readers as otherwise
      // it reads two of the same elements
      datepicker.dateInputElement.setAttribute('aria-hidden', 'true');
      datepicker.dateInputElement.setAttribute('tabindex', '-1');
    }
  }, [datepicker, value, minDate, maxDate, onChange, id]);

  return (
    <span ref={spanRef} className={cn('cp-native-datepicker', className)}>
      <ScreenReaderMessage>
        <label htmlFor={id} className="mobile-datepicker-label" aria-label={labelText}>
          {labelText}
        </label>
      </ScreenReaderMessage>
      {children}
    </span>
  );
}

NativeDatePicker.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  className: PropTypes.string,
  children: childrenShape.isRequired,
  id: PropTypes.string.isRequired
};
